<script setup lang="ts">
/* ============================================================
   InstallModal — "Connect your agent" bootstrap prompts

   Installation is agent-driven: instead of a curl|bash one-liner,
   the user copies a natural-language prompt and pastes it into
   their agent, which fetches /SKILL.md itself and installs it
   where that agent keeps instructions (.claude/skills/, AGENTS.md,
   .cursor/rules/). Pick an agent to preview the exact prompt, then
   copy it.

   Prompts are built from location.origin, so a self-hosted
   instance hands out self-pointing bootstraps with zero config —
   same property as the Worker's origin-rewritten /SKILL.md.
   ============================================================ */

import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { pushToast } from '@/composables/useToasts'

const emit = defineEmits<{ (e: 'close'): void }>()

const origin = location.origin
const skillUrl = `${origin}/SKILL.md`

interface AgentTarget {
  id: string
  name: string
  hint: string
  prompt: string
}

const AGENTS: AgentTarget[] = [
  {
    id: 'claude',
    name: 'Claude Code',
    hint: 'Installs as a skill in .claude/skills/',
    prompt: [
      'redline.ink is a tool for sharing a plan with me for review. Please add',
      'its skill to your setup:',
      '',
      `1. Open ${skillUrl} and read it so you know what it does.`,
      '2. If it looks good, save it to .claude/skills/redline/SKILL.md in this',
      '   project (or ~/.claude/skills/redline/SKILL.md to use it everywhere).',
      '3. Ask me whether to open review links in a browser automatically —',
      '   default is no, just print the link — and set the `open-in-browser:`',
      '   line near the end of the saved file to my answer (no / yes).',
      '4. Tell me in one sentence what it does.',
      '',
      'After that, when you write up a substantial plan, share it with the skill',
      'and give me the review link instead of printing the whole plan.',
    ].join('\n'),
  },
  {
    id: 'codex',
    name: 'Codex CLI',
    hint: 'Adds the workflow to AGENTS.md',
    prompt: [
      'redline.ink is a tool for sharing a plan with me for review. Please add',
      'its workflow to your setup:',
      '',
      `1. Open ${skillUrl} and read it so you know what it does. (If your`,
      `   sandbox blocks the network, tell me and I'll whitelist ${origin.replace(/^https?:\/\//, '')}`,
      '   in ~/.codex/config.toml first.)',
      '2. If it looks good, add everything after the YAML frontmatter to',
      '   AGENTS.md in this repo, under a "## Sharing plans on redline" heading.',
      '3. Ask me whether to open review links in a browser automatically —',
      '   default is no, just print the link — and set the `open-in-browser:`',
      '   line in what you added to my answer (no / yes).',
      '4. Tell me in one sentence what it does.',
      '',
      'After that, when you write up a substantial plan, share it with the',
      'workflow and give me the review link instead of printing the whole plan.',
    ].join('\n'),
  },
  {
    id: 'cursor',
    name: 'Cursor',
    hint: 'Saves as a project rule in .cursor/rules/',
    prompt: [
      'redline.ink is a tool for sharing a plan with me for review. Please add',
      'its workflow to your setup:',
      '',
      `1. Open ${skillUrl} and read it so you know what it does.`,
      '2. If it looks good, save everything after the YAML frontmatter to',
      '   .cursor/rules/redline.mdc so it loads as a project rule.',
      '3. Ask me whether to open review links in a browser automatically —',
      '   default is no, just print the link — and set the `open-in-browser:`',
      '   line in the saved rule to my answer (no / yes).',
      '4. Tell me in one sentence what it does.',
      '',
      'After that, when you write up a substantial plan, share it with the rule',
      'and give me the review link instead of printing the whole plan.',
    ].join('\n'),
  },
]

const selectedId = ref(AGENTS[0].id)
const selected = computed(() => AGENTS.find((a) => a.id === selectedId.value) ?? AGENTS[0])

async function copyPrompt(): Promise<void> {
  try {
    await navigator.clipboard.writeText(selected.value.prompt)
    pushToast(`Prompt copied — paste it into ${selected.value.name}.`)
  } catch {
    pushToast('Could not copy — select the prompt text and copy it manually.', 'error')
  }
}

function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    e.preventDefault()
    emit('close')
  }
}

onMounted(() => document.addEventListener('keydown', onKeydown, true))
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown, true))
</script>

<template>
  <div class="install-modal" role="dialog" aria-modal="true" aria-label="Connect your agent">
    <div class="install-backdrop" @click="emit('close')"></div>
    <div class="install-panel">
      <div class="install-header">
        <h2 class="install-title">Connect your agent</h2>
        <button class="btn btn-ghost btn-sm" aria-label="Close" @click="emit('close')">✕</button>
      </div>

      <div class="install-body">
        <p class="install-blurb">
          Your agent installs redline itself — no shell pipe. Pick your agent, copy the
          prompt, and paste it into a session. The agent fetches the skill and puts it
          where it keeps instructions.
        </p>

        <div class="install-agents" role="radiogroup" aria-label="Agent">
          <button
            v-for="a in AGENTS"
            :key="a.id"
            class="install-agent"
            :class="{ 'is-active': a.id === selectedId }"
            role="radio"
            :aria-checked="a.id === selectedId"
            @click="selectedId = a.id"
          >
            <span class="install-agent-name">{{ a.name }}</span>
            <span class="install-agent-hint">{{ a.hint }}</span>
          </button>
        </div>

        <pre class="install-preview">{{ selected.prompt }}</pre>
      </div>

      <div class="install-actions">
        <a class="install-skill-link" :href="skillUrl" target="_blank" rel="noopener">
          View the skill ↗
        </a>
        <span class="install-actions-buttons">
          <button class="btn btn-default btn-md" @click="emit('close')">Close</button>
          <button class="btn btn-primary btn-md" @click="copyPrompt">
            Copy prompt for {{ selected.name }}
          </button>
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.install-modal {
  position: absolute;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-6);
}
.install-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.32);
  backdrop-filter: blur(2px);
}
:global(:root[data-mode='dark']) .install-backdrop {
  background: rgba(0, 0, 0, 0.55);
}
.install-panel {
  position: relative;
  width: min(680px, 100%);
  max-height: 100%;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg, 0 24px 48px rgba(0, 0, 0, 0.18));
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.install-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--border);
}
.install-title {
  font-size: var(--text-lg);
  font-weight: 600;
  margin: 0;
  color: var(--text);
}
.install-body {
  padding: var(--space-4) var(--space-5);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}
.install-blurb {
  margin: 0;
  color: var(--text-muted);
  font-size: var(--text-md);
  line-height: 1.55;
}
.install-agents {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-2);
}
.install-agent {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: var(--space-3);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--bg);
  text-align: left;
  cursor: pointer;
}
.install-agent:hover {
  background: var(--bg-hover);
}
.install-agent.is-active {
  border-color: var(--accent, var(--border-strong));
  background: var(--bg-hover);
}
.install-agent-name {
  font-weight: 600;
  color: var(--text);
  font-size: var(--text-md);
}
.install-agent-hint {
  color: var(--text-faint);
  font-size: var(--text-sm);
  line-height: 1.35;
}
.install-preview {
  margin: 0;
  padding: var(--space-4);
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  font-family: var(--mono-font);
  font-size: 12px;
  line-height: 1.55;
  color: var(--text-muted);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.install-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-5);
  border-top: 1px solid var(--border);
}
.install-skill-link {
  font-size: var(--text-sm);
  color: var(--text-faint);
  text-decoration: none;
}
.install-skill-link:hover {
  color: var(--text);
  text-decoration: underline;
}
.install-actions-buttons {
  display: flex;
  gap: var(--space-2);
}
@media (max-width: 760px) {
  .install-agents {
    grid-template-columns: 1fr;
  }
}
</style>
